/* eslint-disable no-nested-ternary */
import React, { useContext } from 'react';
import {
  arrayOf,
  bool,
  string,
  number,
  shape
} from 'prop-types';
import classnames from 'classnames/bind';
import { useImpression } from '@thd-olt-component-react/impression';
import { HomepageContext } from './context/HomepageContext';
import componentStyles from './components.module.scss';
import { CUSTOMER_TYPES } from '../utils';
import { selectPreferredImage } from '../utils/publicLinks';

const styles = classnames.bind(componentStyles);

export function HeroFlattenImage({
  id,
  image,
  disableAspectRatio,
  layoutRendererPrerenderedChildIndex,
  link,
  mobileImage,
  mobilePreviewImage,
  previewImage,
  proAnalyticsCampaign,
  proAnalyticsComponent,
  title,
}) {
  const { customerType, proCustomerSegment } = useContext(HomepageContext);

  const impressionContextData = useImpression({
    data: {
      id,
      name: 'HeroFlattenImage',
      component: 'Hero',
      position: layoutRendererPrerenderedChildIndex + 1,
      type: 'content',
    },
  });

  const largeViewportImageSrcUrl = selectPreferredImage({
    damImage: previewImage,
    nonDamImage: image,
  });
  const smallViewportImageSrcUrl = selectPreferredImage({
    damImage: mobilePreviewImage,
    nonDamImage: mobileImage,
  });

  if (!largeViewportImageSrcUrl && !smallViewportImageSrcUrl) {
    return null;
  }

  const isB2B = customerType === CUSTOMER_TYPES.B2B;
  const boxAspectRatio = disableAspectRatio
    ? ''
    : isB2B
      ? 'aspect-ratio-box--b2b'
      : 'aspect-ratio-box';
  const imgAspectRatio = disableAspectRatio
    ? ''
    : isB2B
      ? 'aspect-ratio-img--b2b'
      : 'aspect-ratio-img';
  const mobileBreakpoint = isB2B ? '640px' : '768px';

  function handleClickAnalytics() {
    if (isB2B) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('hero-flatten.content-click', {
        widgetId: id,
        widgetTemplates: proCustomerSegment,
        widgetCampaign: proAnalyticsCampaign,
        widgetComponent: proAnalyticsComponent,
      });
    }
  }

  const altText = title;

  return (

    <a
      href={link}
      key={id}
      className={styles(boxAspectRatio)}
      onClick={handleClickAnalytics}
      ref={impressionContextData.ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={impressionContextData.clickID}
    >
      <picture>
        <source
          className={styles('stretchy')}
          alt={`Image for ${altText}`}
          srcSet={largeViewportImageSrcUrl}
          media={`(min-width: ${mobileBreakpoint})`}
        />
        {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
        <img
          className={styles(imgAspectRatio, 'stretchy')}
          alt={altText}
          src={smallViewportImageSrcUrl}
          loading={layoutRendererPrerenderedChildIndex > 1 ? 'lazy' : 'eager'}
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority={layoutRendererPrerenderedChildIndex < 2 ? 'high' : 'low'}
        />
      </picture>
    </a>
  );
}

HeroFlattenImage.propTypes = {
  link: string,
  id: string,
  image: shape({
    contentType: string,
    id: string,
    description: string,
    publishedDate: string,
    title: string,
    url: string,
  }),
  mobileImage: shape({
    contentType: string,
    id: string,
    description: string,
    publishedDate: string,
    title: string,
    url: string,
  }),
  mobilePreviewImage: shape({
    damContentSelector: shape({
      assetData: arrayOf(
        shape({
          selectedImageUrl: string,
        })
      ),
    }),
    damDownloadedContent: shape({
      url: string,
    }),
  }),
  previewImage: shape({
    damContentSelector: shape({
      assetData: arrayOf(
        shape({
          selectedImageUrl: string,
        })
      ),
    }),
    damDownloadedContent: shape({
      url: string,
    }),
  }),

  title: string,
  layoutRendererPrerenderedChildIndex: number,
  disableAspectRatio: bool,
  proAnalyticsCampaign: string,
  proAnalyticsComponent: string,
};

HeroFlattenImage.defaultProps = {
  link: '',
  id: '',
  image: {},
  mobileImage: {},
  layoutRendererPrerenderedChildIndex: undefined,
  disableAspectRatio: false,
  proAnalyticsCampaign: undefined,
  proAnalyticsComponent: undefined,
  title: undefined,
  mobilePreviewImage: undefined,
  previewImage: undefined,
};
